import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ButtonElement,
  ColorScheme,
  MediaElement,
  TypographyElement,
  useContentElement,
  useContentVisibility,
  VisibilityScheme,
} from '@plugins/next-cms-core';
import { useTranslation } from 'next-i18next';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';
import Container from '../atoms/Container';

const TRANSLATION_PREFIX = 'components.contentTypes.DataTableBlock';

// TypographyElement.propTypes describes the component's own props (`data`, `color`),
// not the CMS payload, so the payload shape is declared here.
const TYPOGRAPHY_SHAPE = PropTypes.shape({
  displayVariant: PropTypes.string,
  displayVariantMobile: PropTypes.string,
  semanticVariant: PropTypes.string,
  textAlign: PropTypes.string,
  value: PropTypes.string,
});

export default function DataTableBlock(props) {
  const { data } = props;
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const { elementData } = useContentElement(
    data,
    DataTableBlock.dataSchema,
  );

  const {
    dataTable,
    defaultSortColumn,
    defaultSortDirection,
    eyebrow,
    visibility,
  } = elementData;

  const table = dataTable?.data?.attributes;
  // Memoised so the `?? []` fallbacks stay referentially stable across renders
  const columns = useMemo(() => table?.columns ?? [], [table]);
  const rows = useMemo(() => table?.rows ?? [], [table]);

  const [sortIndex, setSortIndex] = useState(
    () => getInitialSortIndex(columns, defaultSortColumn),
  );
  const [sortDirection, setSortDirection] = useState(defaultSortDirection ?? 'asc');
  const [filterValue, setFilterValue] = useState(null);

  const filterIndex = columns.findIndex((column) => column.isFilter && column.type === 'category');
  const filterOptions = useMemo(
    () => getFilterOptions(columns[filterIndex], rows, filterIndex),
    [columns, filterIndex, rows],
  );

  const visibleRows = useMemo(() => {
    const filtered = filterIndex >= 0 && filterValue !== null
      ? rows.filter((row) => getCell(row, filterIndex)?.value === filterValue)
      : rows;

    if (sortIndex < 0 || !columns[sortIndex]) {
      return filtered;
    }

    const factor = sortDirection === 'desc' ? -1 : 1;
    return [...filtered].sort(
      (a, b) => compareRows(a, b, sortIndex, columns[sortIndex]) * factor,
    );
  }, [columns, filterIndex, filterValue, rows, sortDirection, sortIndex]);

  const colorScheme = ColorScheme.getColorScheme(elementData.colorScheme, 'default');
  const cssColorClass = `${colorScheme[0].toUpperCase()}${colorScheme.substring(1)}`;

  const rootStyles = useContentVisibility(visibility, {
    paddingTop: {
      xs: theme.spacing(5),
      md: theme.spacing(10),
    },
    paddingBottom: {
      xs: theme.spacing(5),
      md: theme.spacing(10),
    },
    backgroundColor: getRootBackgroundColor(cssColorClass, theme),
  });

  let title = null;

  if (elementData.dataTableTitle?.value) {
    title = omitBy(elementData.dataTableTitle, isNull);
    defaults(title, {
      semanticVariant: 'h3',
      displayVariant: 'h3',
    });
  }

  let footnote = null;

  if (elementData.footnote?.value) {
    footnote = omitBy(elementData.footnote, isNull);
    defaults(footnote, {
      semanticVariant: 'div',
      displayVariant: 'caption',
    });
  }

  const primaryIndex = Math.max(columns.findIndex((column) => column.isPrimary), 0);
  const sublineIndex = columns.findIndex((column) => column.isSubline);
  const sortableColumns = columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.isSortable);

  const handleSort = (index) => {
    if (sortIndex === index) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortIndex(index);
    setSortDirection('asc');
  };

  // A table without columns still renders its heading and footnote, so an
  // unfinished entry does not make the whole block disappear from the page.
  // The relation resolves to null both when no table is picked and when the
  // picked one is unpublished, so those two share the "no table" message.
  const hasTableRelation = Boolean(dataTable?.data);
  const hasColumns = columns.length > 0;
  const locale = i18n.language;

  return (
    <Box sx={rootStyles}>
      <Container>
        {(eyebrow || title) && (
          <Box mb={3}>
            {eyebrow && (
              <Typography color="primary" component="div" variant="overline">
                {eyebrow}
              </Typography>
            )}
            {title && <TypographyElement data={title} />}
          </Box>
        )}

        {filterOptions.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            spacing={1}
            sx={{ mb: 3 }}
            useFlexGap
          >
            <Chip
              color="primary"
              label={t(`${TRANSLATION_PREFIX}.all`)}
              onClick={() => setFilterValue(null)}
              variant={filterValue === null ? 'filled' : 'outlined'}
            />
            {filterOptions.map((option) => (
              <Chip
                key={option.value}
                color={option.color ?? 'default'}
                label={option.value}
                onClick={() => setFilterValue(option.value)}
                variant={filterValue === option.value ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
        )}

        {/*
          The breakpoint switch has to happen in CSS: the theme sets
          MuiUseMediaQuery.ssrMatchMedia to a 0px viewport, so useMediaQuery would
          always report mobile on the server and hydrate incorrectly.
        */}
        {hasColumns && (
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.id}
                      align={column.align ?? 'left'}
                      sortDirection={sortIndex === index ? sortDirection : false}
                      sx={{
                        width: column.width || undefined,
                        ...getColumnCellStyles(column, index === primaryIndex),
                      }}
                    >
                      {column.isSortable ? (
                        <TableSortLabel
                          active={sortIndex === index}
                          direction={sortIndex === index ? sortDirection : 'asc'}
                          onClick={() => handleSort(index)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: 'action.hover',
                      },
                      ...(row.isHighlighted && {
                        backgroundColor: 'action.selected',
                      }),
                    }}
                  >
                    {columns.map((column, index) => (
                      <TableCell
                        key={column.id}
                        align={column.align ?? 'left'}
                        sx={{
                          ...(column.type === 'number' && {
                            fontVariantNumeric: 'tabular-nums',
                          }),
                          ...getColumnCellStyles(column, index === primaryIndex),
                        }}
                      >
                        <CellContent
                          cell={getCell(row, index)}
                          column={column}
                          locale={locale}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        )}

        {hasColumns && (
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {sortableColumns.length > 0 && (
            <TextField
              fullWidth
              label={t(`${TRANSLATION_PREFIX}.sortBy`)}
              onChange={(event) => handleSort(Number(event.target.value))}
              select
              size="small"
              sx={{ mb: 2 }}
              value={sortableColumns.some(({ index }) => index === sortIndex)
                ? String(sortIndex)
                : ''}
            >
              {sortableColumns.map(({ column, index }) => (
                <MenuItem key={column.id} value={String(index)}>
                  {column.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack spacing={2}>
            {visibleRows.map((row) => (
              <MobileRowCard
                key={row.id}
                columns={columns}
                locale={locale}
                primaryIndex={primaryIndex}
                row={row}
                sublineIndex={sublineIndex}
              />
            ))}
          </Stack>
        </Box>
        )}

        {!hasColumns && (
          <Paper sx={{ p: 3, textAlign: 'center' }} variant="outlined">
            <Typography color="textSecondary" component="div" variant="body2">
              {t(`${TRANSLATION_PREFIX}.${hasTableRelation ? 'emptyTable' : 'noTable'}`)}
            </Typography>
          </Paper>
        )}

        {(footnote || filterOptions.length > 0) && (
          <Box mt={2}>
            {filterOptions.length > 0 && (
              <Typography color="textSecondary" component="div" variant="caption">
                {t(`${TRANSLATION_PREFIX}.rowCount`, { rows: visibleRows.length })}
              </Typography>
            )}
            {footnote && <TypographyElement data={footnote} />}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function MobileRowCard(props) {
  const {
    columns,
    locale,
    primaryIndex,
    row,
    sublineIndex,
  } = props;
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);

  // A column flagged as "hide on mobile" is not dropped any more, it moves behind the
  // "show more" toggle — so a subline column carrying that flag loses its subline role and
  // is rendered as one of the collapsed detail rows instead.
  const showSubline = sublineIndex >= 0 && !columns[sublineIndex]?.hideOnMobile;
  const detailColumns = columns
    .map((column, index) => ({ column, index }))
    .filter(({ index }) => index !== primaryIndex
      && !(showSubline && index === sublineIndex));
  const visibleColumns = detailColumns.filter(({ column }) => !column.hideOnMobile);
  const collapsedColumns = detailColumns.filter(({ column }) => column.hideOnMobile);

  return (
    <Card variant="outlined">
      <CardContent>
        {showSubline && (
          <Typography color="textSecondary" component="div" variant="caption">
            <CellContent
              cell={getCell(row, sublineIndex)}
              column={columns[sublineIndex]}
              locale={locale}
            />
          </Typography>
        )}
        <Typography component="div" gutterBottom variant="h6">
          <CellContent
            cell={getCell(row, primaryIndex)}
            column={columns[primaryIndex]}
            locale={locale}
          />
        </Typography>
        <Stack divider={<Divider flexItem />}>
          {visibleColumns.map(({ column, index }) => (
            <MobileDetailRow
              key={column.id}
              cell={getCell(row, index)}
              column={column}
              locale={locale}
            />
          ))}
        </Stack>
        {collapsedColumns.length > 0 && (
          <>
            <Collapse in={isExpanded}>
              <Stack
                divider={<Divider flexItem />}
                sx={visibleColumns.length > 0 ? {
                  borderTop: 1,
                  borderTopColor: 'divider',
                } : undefined}
              >
                {collapsedColumns.map(({ column, index }) => (
                  <MobileDetailRow
                    key={column.id}
                    cell={getCell(row, index)}
                    column={column}
                    locale={locale}
                  />
                ))}
              </Stack>
            </Collapse>
            <Box mt={1}>
              <Button
                fullWidth
                onClick={() => setExpanded(!isExpanded)}
                size="small"
                startIcon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
              >
                {t(`${TRANSLATION_PREFIX}.${isExpanded ? 'showLess' : 'showMore'}`)}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

MobileRowCard.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  locale: PropTypes.string.isRequired,
  primaryIndex: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired,
  sublineIndex: PropTypes.number.isRequired,
};

function MobileDetailRow(props) {
  const { cell, column, locale } = props;

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 1 }}
    >
      <Typography color="textSecondary" variant="body2">
        {column.label}
      </Typography>
      <Box
        sx={{
          textAlign: 'right',
          ...(column.type === 'number' && {
            fontVariantNumeric: 'tabular-nums',
          }),
          ...(column.noWrap && { whiteSpace: 'nowrap' }),
        }}
      >
        <CellContent cell={cell} column={column} locale={locale} />
      </Box>
    </Stack>
  );
}

MobileDetailRow.propTypes = {
  cell: PropTypes.object,
  column: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
};

function CellContent(props) {
  const { cell, column, locale } = props;

  if (!cell) {
    return null;
  }

  switch (column.type) {
    case 'image':
      return cell.media
        ? (
          <Box sx={{ maxWidth: 120 }}>
            <MediaElement data={cell.media} format="small" isFluid />
          </Box>
        )
        : null;
    case 'link':
      return cell.link
        ? (
          <ButtonElement
            component="a"
            data={cell.link}
            size="small"
            variant="outlined"
          />
        )
        : null;
    case 'category':
      return cell.value
        ? (
          <Chip
            color={getCategoryColor(column, cell.value)}
            label={cell.value}
            size="small"
          />
        )
        : null;
    case 'number':
      return formatNumberCell(cell, column, locale);
    default:
      return cell.valueHtml
        ? <MarkdownCellValue value={cell.valueHtml} />
        : cell.value ?? null;
  }
}

CellContent.propTypes = {
  cell: PropTypes.object,
  column: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
};

/**
 * Text cells are authored as markdown so editors can use bullet lists and line breaks. The
 * GraphQL layer renders them into `valueHtml` and leaves `value` raw, because sorting,
 * filtering and category matching still compare against the plain text.
 */
function MarkdownCellValue(props) {
  const { value } = props;

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: value }}
      sx={{
        '& p': {
          margin: 0,
        },
        '& p + p': {
          marginTop: 1,
        },
        // Lists stay left aligned even in the right aligned mobile detail rows, where
        // trailing bullet markers would otherwise sit in the middle of the card.
        '& ul, & ol': {
          margin: 0,
          paddingLeft: 2.5,
          textAlign: 'left',
        },
      }}
    />
  );
}

MarkdownCellValue.propTypes = {
  value: PropTypes.string.isRequired,
};

/**
 * The primary column holds the row's label, so the desktop table emphasises it the way the
 * mobile card already does. Bold plus a divider survives the zebra striping and the colour
 * schemes, which a background colour of its own would fight with.
 */
function getColumnCellStyles(column, isPrimaryColumn) {
  return {
    ...(column.noWrap && { whiteSpace: 'nowrap' }),
    ...(isPrimaryColumn && {
      fontWeight: 600,
      borderRight: 1,
      borderRightColor: 'divider',
    }),
  };
}

function getCell(row, index) {
  if (index < 0) {
    return null;
  }

  return row.cells?.[index] ?? null;
}

function getCategoryColor(column, value) {
  return column.categories?.find((category) => category.value === value)?.color ?? 'default';
}

/**
 * Filter chips are driven by the categories the editor defined on the column, so
 * they keep the editorial order. Tables that only fill in cell values still get
 * chips, derived from the distinct values in document order.
 */
function getFilterOptions(column, rows, index) {
  if (!column) {
    return [];
  }

  if (column.categories?.length) {
    return column.categories;
  }

  const seen = new Set();
  const options = [];

  rows.forEach((row) => {
    const { value } = getCell(row, index) ?? {};

    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ color: 'default', value });
    }
  });

  return options;
}

function getInitialSortIndex(columns, defaultSortColumn) {
  if (!Number.isInteger(defaultSortColumn)) {
    return -1;
  }

  const index = defaultSortColumn - 1;

  return index >= 0 && index < columns.length ? index : -1;
}

function formatNumberCell(cell, column, locale) {
  if (cell.number === null || cell.number === undefined) {
    return cell.value ?? null;
  }

  const options = Number.isInteger(column.decimals)
    ? {
      maximumFractionDigits: column.decimals,
      minimumFractionDigits: column.decimals,
    }
    : {};

  return [
    column.prefix,
    new Intl.NumberFormat(locale, options).format(cell.number),
    column.unit,
  ].filter(Boolean).join(' ');
}

/**
 * Numbers sort on the dedicated numeric field, everything else on the text value.
 * Empty cells always sort last, independent of the direction.
 */
function compareRows(rowA, rowB, index, column) {
  const cellA = getCell(rowA, index);
  const cellB = getCell(rowB, index);

  if (column.type === 'number') {
    const numberA = cellA?.number;
    const numberB = cellB?.number;

    if (numberA === null || numberA === undefined) {
      return numberB === null || numberB === undefined ? 0 : 1;
    }
    if (numberB === null || numberB === undefined) {
      return -1;
    }

    return numberA - numberB;
  }

  const valueA = cellA?.value ?? '';
  const valueB = cellB?.value ?? '';

  if (!valueA) {
    return valueB ? 1 : 0;
  }
  if (!valueB) {
    return -1;
  }

  return valueA.localeCompare(valueB);
}

function getRootBackgroundColor(variant, theme) {
  switch (variant) {
    case 'Primary':
      return theme.palette.secondary.main;
    case 'Secondary':
      return theme.palette.primary.main;
    case 'Tertiary':
      return theme.palette.tertiary.main;
    case 'Quaternary':
      return theme.palette.quaternary.main;
    case 'Light':
      return theme.palette.light.main;
    case 'Dark':
      return theme.palette.dark.main;
    case 'MitsubishiYellow':
      return '#f5ba45';
    default:
      return 'transparent';
  }
}

DataTableBlock.typeName = 'ComponentContentDataTable'; // Strapi element type
DataTableBlock.propTypes = {
  data: PropTypes.shape({
    eyebrow: PropTypes.string,
    dataTableTitle: TYPOGRAPHY_SHAPE,
    footnote: TYPOGRAPHY_SHAPE,
    defaultSortColumn: PropTypes.number,
    defaultSortDirection: PropTypes.oneOf([null, 'asc', 'desc']),
    dataTable: PropTypes.object,
    colorScheme: PropTypes.shape(ColorScheme.propTypes),
    visibility: PropTypes.shape(VisibilityScheme.propTypes),
  }).isRequired,
};

DataTableBlock.graphQlSchema = `
... on ${DataTableBlock.typeName} {
  id
  eyebrow
  defaultSortColumn
  defaultSortDirection
  dataTableTitle: title {
    ${TypographyElement.graphQlSchema}
  }
  footnote {
    ${TypographyElement.graphQlSchema}
  }
  dataTable {
    data {
      id
      attributes {
        name
        columns(pagination: {limit: 20}) {
          id
          label
          type
          unit
          prefix
          decimals
          align
          isSortable
          isFilter
          isPrimary
          isSubline
          hideOnMobile
          noWrap
          width
          categories(pagination: {limit: 20}) {
            id
            value
            color
          }
        }
        rows(pagination: {limit: 200}) {
          id
          isHighlighted
          cells(pagination: {limit: 20}) {
            id
            value
            valueHtml
            number
            media {
              ${MediaElement.graphQlSchema}
            }
            link {
              ${ButtonElement.graphQlSchema}
            }
          }
        }
      }
    }
  }
  colorScheme {
    ${ColorScheme.graphQlSchema}
  }
  visibility {
    ${VisibilityScheme.graphQlSchema}
  }
}
`;
